/**
 * HTML da proposta comercial Amazon Energy (PDF via html2canvas).
 * Usado pelo módulo compartilhado src/utils/propostaDocumento (Calculadora Solar
 * e Consulta de Propostas). Placeholders escapados pelo caller.
 */

export const escapeHtmlAmazon = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

/**
 * @param {object} p
 * @param {string} p.nomeCliente
 * @param {string} p.kwpStr — ex. "3,5 kWp"
 * @param {string} p.geracaoStr — ex. "500 kWh/mês"
 * @param {string} p.investimentoStr — ex. "R$ 15.600,25"
 * @param {string} p.inversorStr
 * @param {string} p.modulosStr
 * @param {string} p.empresaNome
 * @param {string} p.slogan
 * @param {string} p.logoInnerHtml — HTML seguro do logo ou placeholder
 * @param {string} p.localizacaoStr
 * @param {string} p.contatoStr — linha de contato
 * @param {string[]} p.paybackLabels — 5 rótulos dos topos das barras
 * @param {[string,string,string,string]} p.gallerySrc — URLs dos 4 cards
 */
export function buildAmazonEnergyPropostaHtml(p) {
  const {
    nomeCliente,
    kwpStr,
    geracaoStr,
    investimentoStr,
    inversorStr,
    modulosStr,
    empresaNome,
    slogan,
    logoInnerHtml,
    localizacaoStr,
    contatoStr,
    paybackLabels,
    gallerySrc
  } = p

  const [pb0, pb1, pb2, pb3, pb4] = paybackLabels

  return `<!DOCTYPE html>
<html lang="pt-br">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Proposta Comercial - ${empresaNome}</title>
    <style>
        :root {
            --primary: #30F37B;
            --secondary: #000000;
            --gray-light: #f4f7f6;
            --text-dark: #333;
            --text-muted: #666;
        }
        * { margin: 0; padding: 0; box-sizing: border-box; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; }
        body { background-color: #fff; color: var(--text-dark); }
        .page {
            background: white;
            width: 210mm;
            min-height: 297mm;
            padding: 15mm 20mm;
            position: relative;
            display: flex;
            flex-direction: column;
        }
        .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid var(--primary); padding-bottom: 15px; margin-bottom: 25px; }
        .logo-area { width: 200px; height: 80px; background: var(--primary); border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 12px; color: #999; overflow: hidden; padding: 6px; box-sizing: border-box; }
        .logo-area img { max-width: 100%; max-height: 100%; width: auto; height: auto; object-fit: contain; }
        .company-info { text-align: right; }
        .company-info h1 { color: var(--secondary); font-size: 24px; letter-spacing: 1px; }
        h2 { border-left: 5px solid var(--primary); padding-left: 10px; margin: 20px 0 10px; font-size: 18px; text-transform: uppercase; color: var(--secondary); }
        .data-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; background: var(--secondary); padding: 20px; border-radius: 8px; color: white; margin-bottom: 20px; }
        .data-item { border-bottom: 1px solid #333; padding: 5px 0; font-size: 14px; }
        .data-item span { color: var(--primary); font-weight: bold; }
        .how-it-works { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 20px 0; }
        .step { text-align: center; background: var(--gray-light); padding: 10px; border-radius: 8px; }
        .step svg { width: 40px; fill: var(--secondary); margin-bottom: 8px; }
        .step h4 { font-size: 13px; margin-bottom: 5px; color: var(--secondary); }
        .step p { font-size: 10px; color: var(--text-muted); line-height: 1.3; }
        .advantages { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
        .adv-card { border: 1px solid #eee; padding: 12px; border-radius: 8px; display: flex; align-items: center; background: #fff; }
        .adv-icon { font-size: 24px; margin-right: 12px; }
        .adv-text strong { font-size: 14px; display: block; }
        .adv-text small { font-size: 11px; color: var(--text-muted); }
        .payback-chart { margin: 25px 0; background: #fff; padding: 15px; border: 1px solid #eee; border-radius: 8px; }
        .bars { display: flex; align-items: flex-end; height: 150px; gap: 10px; border-bottom: 2px solid var(--secondary); padding-bottom: 5px; margin-bottom: 10px; }
        .bar { flex: 1; background: #ddd; position: relative; border-radius: 4px 4px 0 0; }
        .bar.positive { background: var(--primary); }
        .bar span { position: absolute; top: -20px; width: 100%; text-align: center; font-size: 10px; font-weight: bold; }
        .labels { display: flex; justify-content: space-around; font-size: 12px; font-weight: bold; }
        .gallery { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-top: 15px; }
        .inst-card { border-radius: 6px; overflow: hidden; height: 130px; border: 1px solid #ddd; position: relative; }
        .inst-card img { width: 100%; height: 100%; object-fit: cover; }
        .inst-card .label { position: absolute; bottom: 0; background: rgba(0,0,0,0.8); color: white; width: 100%; font-size: 9px; padding: 3px; text-align: center; }
        .faq { margin-top: 20px; }
        .faq-item { margin-bottom: 12px; background: var(--gray-light); padding: 12px; border-radius: 6px; }
        .faq-item strong { display: block; font-size: 13px; color: var(--secondary); margin-bottom: 4px; }
        .faq-item p { font-size: 12px; color: var(--text-muted); }
        .footer-elaborated { margin-top: auto; padding-top: 30px; }
        .signatures { display: flex; justify-content: space-between; margin-bottom: 30px; }
        .sig-block { width: 45%; text-align: center; }
        .sig-line { border-top: 1px solid var(--secondary); margin-bottom: 8px; }
        .sig-block p { font-size: 12px; color: var(--text-muted); }
        .legal-box { font-size: 10px; color: #888; background: #f9f9f9; padding: 10px; border-radius: 4px; margin-bottom: 20px; border: 1px solid #eee; }
        .contact-bar { background: var(--secondary); color: white; padding: 15px; border-radius: 8px; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px; }
        .contact-info { font-size: 11px; }
        .contact-info strong { color: var(--primary); font-size: 13px; display: block; }
    </style>
</head>
<body>
    <div class="page">
        <div class="header">
            <div class="logo-area">${logoInnerHtml}</div>
            <div class="company-info">
                <h1>${empresaNome}</h1>
                <p style="font-size: 12px; color: #666;">${slogan}</p>
            </div>
        </div>
        <div class="intro">
            <p style="font-size: 14px;">Prezado(a) <strong>${nomeCliente}</strong>,</p>
            <p style="font-size: 13px; margin-top: 5px; color: #555;">Apresentamos abaixo a configuração técnica e financeira do seu sistema fotovoltaico personalizado.</p>
        </div>
        <h2>Dados da Proposta</h2>
        <div class="data-grid">
            <div class="data-item">Cliente: <span>${nomeCliente}</span></div>
            <div class="data-item">Projeto em Kwp: <span>${kwpStr}</span></div>
            <div class="data-item">Geração Estimada: <span>${geracaoStr}</span></div>
            <div class="data-item">Investimento Total: <span>${investimentoStr}</span></div>
            <div class="data-item">Inversor: <span>${inversorStr}</span></div>
            <div class="data-item">Módulos: <span>${modulosStr}</span></div>
        </div>
        <h2>Como Funciona o Sistema</h2>
        <div class="how-it-works">
            <div class="step">
                <svg viewBox="0 0 24 24"><path d="M12,7L11,10H8L11,14L10,17L13,14H16L13,10L14,7H12M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2Z"/></svg>
                <h4>1. Geração</h4>
                <p>Os painéis convertem a luz solar em energia limpa.</p>
            </div>
            <div class="step">
                <svg viewBox="0 0 24 24"><path d="M21,16.5L12,21.5L3,16.5V7.5L12,2.5L21,7.5V16.5Z"/></svg>
                <h4>2. Conversão</h4>
                <p>O Inversor transforma a energia para o uso na sua casa.</p>
            </div>
            <div class="step">
                <svg viewBox="0 0 24 24"><path d="M12,3L2,12H5V20H19V12H22L12,3Z"/></svg>
                <h4>3. Consumo</h4>
                <p>Sua casa usa a energia gerada, reduzindo o custo imediato.</p>
            </div>
            <div class="step">
                <svg viewBox="0 0 24 24"><path d="M12,2C6.47,2 2,6.47 2,12C2,17.53 6.47,22 12,22C17.53,22 22,17.53 22,12C22,6.47 17.53,2 12,2Z"/></svg>
                <h4>4. Créditos</h4>
                <p>O excesso é enviado para a rede e vira créditos de energia.</p>
            </div>
        </div>
        <h2>Vantagens Estratégicas</h2>
        <div class="advantages">
            <div class="adv-card">
                <span class="adv-icon">📉</span>
                <div class="adv-text"><strong>Economia de até 85%</strong><small>Redução drástica na sua fatura mensal.</small></div>
            </div>
            <div class="adv-card">
                <span class="adv-icon">🛡️</span>
                <div class="adv-text"><strong>Garantia de 25 anos</strong><small>Segurança e durabilidade do equipamento.</small></div>
            </div>
            <div class="adv-card">
                <span class="adv-icon">🌳</span>
                <div class="adv-text"><strong>Impacto Ambiental</strong><small>Energia 100% limpa e renovável.</small></div>
            </div>
            <div class="adv-card">
                <span class="adv-icon">💎</span>
                <div class="adv-text"><strong>Valorização do Imóvel</strong><small>Imóveis com energia solar valem até 10% mais.</small></div>
            </div>
        </div>
        <h2>Retorno de Investimento (Payback)</h2>
        <div class="payback-chart">
            <div class="bars">
                <div class="bar" style="height: 20%;"><span>${pb0}</span></div>
                <div class="bar" style="height: 40%;"><span>${pb1}</span></div>
                <div class="bar" style="height: 60%;"><span>${pb2}</span></div>
                <div class="bar" style="height: 80%;"><span>${pb3}</span></div>
                <div class="bar positive" style="height: 100%;"><span>${pb4}</span></div>
            </div>
            <div class="labels">
                <div>Ano 1</div><div>Ano 2</div><div>Ano 3</div><div>Ano 4</div><div>Ano 5</div>
            </div>
        </div>
        <h2>Instalações de Referência</h2>
        <div class="gallery">
            <div class="inst-card"><img crossorigin="anonymous" src="${gallerySrc[0]}"><div class="label">Residencial - 5kWp</div></div>
            <div class="inst-card"><img crossorigin="anonymous" src="${gallerySrc[1]}"><div class="label">Comercial - 15kWp</div></div>
            <div class="inst-card"><img crossorigin="anonymous" src="${gallerySrc[2]}"><div class="label">Industrial - 30kWp</div></div>
            <div class="inst-card"><img crossorigin="anonymous" src="${gallerySrc[3]}"><div class="label">Rural - 10kWp</div></div>
        </div>
        <div class="faq">
            <h2>Dúvidas Frequentes</h2>
            <div class="faq-item">
                <strong>O sistema produz energia à noite ou em dias nublados?</strong>
                <p>À noite não há geração, mas você utiliza os créditos gerados durante o dia. Em dias nublados, a geração ocorre em menor escala devido à radiação difusa.</p>
            </div>
            <div class="faq-item">
                <strong>Qual a durabilidade dos painéis?</strong>
                <p>Os painéis têm garantia de performance de 25 anos, operando com pelo menos 80% da sua capacidade original após este período.</p>
            </div>
            <div class="faq-item">
                <strong>O que acontece se a rede da concessionária cair?</strong>
                <p>Por segurança, sistemas on-grid se desligam automaticamente para evitar o "ilhamento", protegendo as equipes de manutenção da rede elétrica.</p>
            </div>
        </div>
        <div class="footer-elaborated">
            <div class="signatures">
                <div class="sig-block">
                    <div class="sig-line"></div>
                    <strong>${empresaNome}</strong>
                    <p>Consultor Técnico</p>
                </div>
                <div class="sig-block">
                    <div class="sig-line"></div>
                    <strong>${nomeCliente}</strong>
                    <p>Aceite do Cliente</p>
                </div>
            </div>
            <div class="legal-box">
                <strong>Notas Legais:</strong> Esta proposta tem validade de 7 dias úteis. Os valores estão sujeitos a alteração de acordo com a disponibilidade de estoque e variação cambial. A estimativa de economia baseia-se na tarifa atual da concessionária e média histórica de radiação solar local.
            </div>
            <div class="contact-bar">
                <div class="contact-item">
                    <strong>Localização</strong>
                    ${localizacaoStr}
                </div>
                <div class="contact-item">
                    <strong>Contato</strong>
                    ${contatoStr}
                </div>
                <div style="background: white; color: black; padding: 5px; border-radius: 4px; font-size: 8px; font-weight: bold;">
                    SCAN ME<br>PORTFOLIO
                </div>
            </div>
        </div>
    </div>
</body>
</html>`
}
